// src/lib/marketing/competitor-pricing.ts
//
// Competitor prices and free access, as each company's own page showed them on
// the day we checked. ONE source for the landing comparison table and the
// pricing page's comparison line.
//
// External QA (2026-10): the prices had no billing basis ("$49–149/mo" hid that
// Acely's $49 is a $588 annual charge) and no checked date, and Acely was
// marked as having no free version when its pricing page offers one.
//
// Rules for editing:
//   - Copy the plan name, upfront charge and billing period / access length as
//     the competitor's page states them. No ranges that blend plans, no
//     per-month figures the page doesn't charge.
//   - Re-check every entry when you touch one, and move CHECKED_ON.
//   - A page we couldn't read is `verified: false` and renders as
//     "not verified" — never a remembered or guessed number.
//   - Facts only: no strike-throughs, no adjectives about a competitor.

/** The day every entry below was checked against its source page. */
export const COMPETITOR_PRICES_CHECKED_ON = '2026-10-11'

export interface CompetitorPlan {
  /** The plan name as the competitor labels it. */
  name: string
  /** What is charged up front, e.g. "$588". */
  upfront: string
  /** Billing period or access length, e.g. "billed once a year (12 months)". */
  billing: string
}

export interface CompetitorPricing {
  key: 'ka' | 'ac' | 'op' | 'pr'
  name: string
  /** Whether the competitor offers any free way to start. */
  hasFreeAccess: boolean
  /** What the free access is, in the competitor's terms. */
  freeAccess: string
  /** The paid plans we checked (not necessarily every product they sell). */
  plans: CompetitorPlan[]
  /** Anything we did not check, said plainly. */
  note?: string
  /** One short line for the comparison table's Price row. */
  priceSummary: string
  sourceUrl: string
  sourceLabel: string
  /** False when the source page could not be read on CHECKED_ON. */
  verified: boolean
}

export const COMPETITOR_PRICING: CompetitorPricing[] = [
  {
    key: 'ka',
    name: 'Khan Academy',
    hasFreeAccess: true,
    freeAccess: 'Official Digital SAT Prep is free.',
    plans: [],
    note: 'khanacademy.org did not render for our check; the College Board page states the prep is free.',
    priceSummary: 'Free',
    sourceUrl: 'https://blog.collegeboard.org/college-board-khan-academy-for-better-sat-prep',
    sourceLabel: 'College Board',
    verified: true,
  },
  {
    key: 'ac',
    name: 'Acely',
    hasFreeAccess: true,
    freeAccess:
      'A free version with no credit card: a short skills assessment, one SAT practice test and one ACT practice test. All paid plans also have a 3-day free trial.',
    plans: [
      { name: 'Monthly', upfront: '$149', billing: 'billed every month' },
      { name: 'Quarterly', upfront: '$297', billing: 'billed every 3 months' },
      { name: 'Annual', upfront: '$588', billing: 'billed once a year (12 months)' },
    ],
    priceSummary: '$149 monthly · $297 quarterly · $588 yearly',
    sourceUrl: 'https://acely.com/pricing',
    sourceLabel: 'acely.com/pricing',
    verified: true,
  },
  {
    key: 'op',
    name: 'OnePrep',
    hasFreeAccess: true,
    freeAccess:
      'A $0 Free plan with no credit card: limited practice questions, one full-length practice test and AI tutor use limited to 10 “orbs” a week.',
    plans: [
      { name: 'Plus', upfront: '$115', billing: 'one-time, access through November 8, 2026' },
      { name: 'Pro', upfront: '$131.66', billing: 'one-time, access through November 8, 2026' },
    ],
    note: 'Prices as listed for the November 2026 SAT; access ends on that date.',
    priceSummary: '$115 or $131.66 one-time (to Nov 8, 2026)',
    sourceUrl: 'https://www.oneprep.com/pricing',
    sourceLabel: 'oneprep.com/pricing',
    verified: true,
  },
  {
    key: 'pr',
    name: 'Princeton Review',
    hasFreeAccess: true,
    freeAccess: 'A 14-day free trial of SAT Self-Paced, and free SAT practice tests.',
    plans: [{ name: 'SAT Self-Paced', upfront: '$299', billing: '365 days of access' }],
    note: 'We checked the self-paced course only; live courses and tutoring are priced separately.',
    priceSummary: '$299 self-paced (365 days)',
    sourceUrl: 'https://www.princetonreview.com/college/sat-selfguided-course',
    sourceLabel: 'princetonreview.com',
    verified: true,
  },
]

export function competitorPricing(key: CompetitorPricing['key']): CompetitorPricing {
  const c = COMPETITOR_PRICING.find((p) => p.key === key)
  if (!c) throw new Error(`unknown competitor ${key}`)
  return c
}

/** "October 11, 2026" (a calendar date, so formatted in UTC). */
export function competitorCheckedOnLabel(ymd: string = COMPETITOR_PRICES_CHECKED_ON): string {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** The price shown for a competitor: its summary, or "Not verified". */
export function competitorPriceCell(c: CompetitorPricing): string {
  return c.verified ? c.priceSummary : 'Not verified'
}
